import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faUser, faBrain, faDatabase, faCaretDown, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../../services/clinicsService';
import ConsultedDataViewer from '../../components/ui/ConsultedDataViewer';

interface ChatMessageItemProps {
  msg: ChatMessage;
  isStreaming?: boolean;
  isLastPlaceholder?: boolean;
}

const ChatMessageItem: React.FC<ChatMessageItemProps> = ({ msg, isStreaming, isLastPlaceholder }) => {
  const { t } = useTranslation();
  const isUser = msg.role === 'user';

  // Custom renderer for inline links
  const renderers = {
    a: ({ node, href, children, ...props }: any) => {
      if (!href) return <a {...props}>{children}</a>;

      const isPdfLink = href.toLowerCase().endsWith('.pdf') ||
        String(children).toLowerCase().endsWith('.pdf');

      if (isPdfLink) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 rounded-md text-xs font-medium transition-colors no-underline align-middle"
            title="Abrir documento original"
            {...props}
          >
            <FontAwesomeIcon icon={faFilePdf} /> {children}
          </a>
        );
      }

      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline"
          {...props}
        >
          {children}
        </a>
      );
    }
  };

  return (
    <div data-ui="chat-message-item-region-001" className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div data-ui="chat-message-item-region-002" className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${isUser ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white' : 'bg-white/10 text-white/70 border border-white/20'}`}>
        <FontAwesomeIcon icon={isUser ? faUser : faRobot} className={`text-sm ${isUser ? '' : 'clinics-chat-bot-icon'}`} />
      </div>

      <div data-ui="chat-message-item-region-003" className={`flex flex-col gap-1 max-w-[85%] ${isUser ? 'items-end' : 'items-start'}`}>
        <div data-ui="chat-message-item-region-004" className={`px-4 py-3 rounded-2xl shadow-lg ${isUser ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-tr-none' : 'bg-black/75 text-white/90 border border-white/10 rounded-tl-none'} text-sm leading-relaxed whitespace-pre-wrap`}>
          {isUser ? (
            msg.content
          ) : (
            msg.content ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={renderers}
                className="prose prose-invert prose-sm max-w-none break-words"
              >
                {msg.content}
              </ReactMarkdown>
            ) : (
              isStreaming && isLastPlaceholder ? <span className="animate-pulse">Thinking...</span> : null
            )
          )}
        </div>

        {/* Metadata Sections */}
        {!isUser && (msg.reasoning || msg.consulted_data) && (
          <div data-ui="chat-message-item-region-005" className="mt-4 pt-3 border-t border-white/10 space-y-2 text-xs w-full">
            {msg.reasoning && (
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-blue-300 hover:text-blue-200 list-none select-none">
                  <FontAwesomeIcon icon={faBrain} />
                  <span className="font-medium">{t('clinics.chat.reasoningProcess')}</span>
                  <FontAwesomeIcon icon={faCaretDown} className="ml-auto transition-transform group-open:rotate-180" />
                </summary>
                <div data-ui="chat-message-item-region-006" className="mt-2 p-3 bg-black/30 rounded-lg text-white/70 whitespace-pre-wrap font-mono text-[11px]">
                  {msg.reasoning}
                </div>
              </details>
            )}

            {msg.consulted_data && Object.keys(msg.consulted_data).length > 0 && (
              <details className="group">
                <summary className="flex items-center gap-2 cursor-pointer text-green-300 hover:text-green-200 list-none select-none">
                  <FontAwesomeIcon icon={faDatabase} />
                  <span className="font-medium">{t('clinics.chat.consultedData')}</span>
                  <FontAwesomeIcon icon={faCaretDown} className="ml-auto transition-transform group-open:rotate-180" />
                </summary>
                <ConsultedDataViewer data={msg.consulted_data} />
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessageItem;
